import React, { useEffect, useMemo, useState } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from "reactstrap"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useQuery, useMutation } from "@apollo/client"
import gql from "graphql-tag"
import Select from "react-select"
import Loader from "../../Material/Loader"
import "./Modals.css"
import { TPAConfigFormSchema } from "./TPAModalsSchema"

const GetTPAConfigQuery = gql`
  query GetTPAConfig($id: ID!) {
    tpaConfig(id: $id) {
      id
      dateAdded
      dateModified
      claimType
      startDate
      endDate
      communicationChannel
      lineOfBusiness {
        id
        name
      }
      tpa {
        id
        name
      }
    }
  }
`

const TPAConfigCommunicationChannelChoicesQuery = gql`
  query TPAConfigCommunicationChannelChoices {
    tpaConfigCommunicationChannelChoices: __type(name: "IncidentsTPAConfigCommunicationChannelChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

const TPAConfigClaimTypeChoicesQuery = gql`
  query TPAConfigClaimTypeChoices {
    tpaConfigClaimTypeChoices: __type(name: "IncidentsTPAConfigClaimTypeChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

const AllLineOfBusinessQuery = gql`
  query AllLineOfBusiness {
    allLineOfBusiness {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

const AllTPAsQuery = gql`
  query AllTPAs {
    allTpas {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

const CreateTPAConfigMutation = gql`
  mutation CreateTPAConfig($input: CreateTPAConfigMutationInput!) {
    createTpaConfig(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const UpdateTPAConfigMutation = gql`
  mutation UpdateTPAConfig($input: UpdateTPAConfigMutationInput!) {
    updateTpaConfig(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const CreateUpdateTPAConfigModal = ({ isOpen, toggle, id, refetchQuery }) => {
  const [mutationError, setMutationError] = useState(null)
  const isUpdate = !!id
  const { data: tpaConfigData, loading: tpaConfigLoading } = useQuery(GetTPAConfigQuery, {
    variables: { id },
    skip: !isUpdate,
    fetchPolicy: "network-only"
  })
  const { data: commChannelData, loading: commChannelLoading } = useQuery(TPAConfigCommunicationChannelChoicesQuery, { fetchPolicy: "network-only" })
  const { data: claimTypeData, loading: claimTypeLoading } = useQuery(TPAConfigClaimTypeChoicesQuery, { fetchPolicy: "network-only" })
  const { data: lineOfBusinessData, loading: lobLoading } = useQuery(AllLineOfBusinessQuery, { fetchPolicy: "network-only" })
  const { data: tpaData, loading: tpaLoading } = useQuery(AllTPAsQuery, { fetchPolicy: "network-only" })

  const [createTpaConfig, { loading: creating }] = useMutation(CreateTPAConfigMutation)
  const [updateTpaConfig, { loading: updating }] = useMutation(UpdateTPAConfigMutation)

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(TPAConfigFormSchema)
  })


  const claimTypeOptions = useMemo(() =>
    claimTypeData?.tpaConfigClaimTypeChoices?.enumValues?.map(opt => ({
      value: opt.name,
      label: opt.description || opt.name
    })) || [], [claimTypeData]
  )
  const commChannelOptions = useMemo(() =>
    commChannelData?.tpaConfigCommunicationChannelChoices?.enumValues?.map(opt => ({
      value: opt.name,
      label: opt.description || opt.name
    })) || [], [commChannelData]
  )
  const lineOfBusinessOptions = useMemo(() =>
    lineOfBusinessData?.allLineOfBusiness?.edges?.map(({ node }) => ({
      value: node.id,
      label: node.name
    })) || [], [lineOfBusinessData]
  )
  const tpaOptions = useMemo(() =>
    tpaData?.allTpas?.edges?.map(({ node }) => ({
      value: node.id,
      label: node.name
    })) || [], [tpaData]
  )

  useEffect(() => {
    if (isUpdate && tpaConfigData?.tpaConfig && claimTypeOptions.length > 0 && commChannelOptions.length > 0 && lineOfBusinessOptions.length > 0 && tpaOptions.length > 0) {
      reset({
        claimType: tpaConfigData.tpaConfig.claimType ?? null,
        communicationChannel: tpaConfigData.tpaConfig.communicationChannel ?? null,
        startDate: tpaConfigData.tpaConfig.startDate ?? null,
        endDate: tpaConfigData.tpaConfig.endDate ?? null,
        lineOfBusiness: tpaConfigData.tpaConfig.lineOfBusiness?.id ?? null,
        tpa: tpaConfigData.tpaConfig.tpa?.id ?? null,
      })
    } else {
      reset({
        claimType: null,
        communicationChannel: null,
        startDate: null,
        endDate: null,
        lineOfBusiness: null,
        tpa: null,
      })
    }
  }, [tpaConfigData, isUpdate, reset, claimTypeOptions, commChannelOptions, lineOfBusinessOptions, tpaOptions])

  const onSubmit = async (formData) => {
    try {
      setMutationError(null)
      const payload = {
        ...formData,
        claimType: formData.claimType,
        communicationChannel: formData.communicationChannel,
        lineOfBusiness: formData.lineOfBusiness,
        tpa: formData.tpa,
      }
      let result
      if (isUpdate) {
        result = await updateTpaConfig({ variables: { input: { ...payload, tpaConfigId: id } } })
      } else {
        result = await createTpaConfig({ variables: { input: payload } })
      }
      const mutationResult = result?.data?.createTpaConfig || result?.data?.updateTpaConfig
      if (mutationResult && mutationResult.ok) {
        toggle()
        if (refetchQuery) await refetchQuery()
      } else if (mutationResult?.errors && mutationResult.errors.length > 0) {
        setMutationError(mutationResult.errors.map(e => e.messages.join(", ")).join(" | "))
      } else {
        setMutationError("Unknown error occurred.")
      }
    } catch (error) {
      setMutationError(error.message || "Unknown error occurred.")
    }

  }

  const loading = tpaConfigLoading || commChannelLoading || claimTypeLoading || lobLoading || tpaLoading

  return (
    <Modal className="tpa-modal modal-dialog-centered" isOpen={isOpen} size="lg">
      <ModalHeader>{isUpdate ? "Update TPA Config" : "Create TPA Config"}</ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          {loading ? <Loader /> : (
            <>
              <FormGroup>
                <Label for="tpa">TPA</Label>
                <Controller
                  name="tpa"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      options={tpaOptions}
                      placeholder="Select TPA"
                      className={errors.tpa ? "is-invalid" : ""}
                      value={tpaOptions.find(opt => opt.value === field.value) || null}
                      onChange={opt => field.onChange(opt ? opt.value : '')}
                    />
                  )}
                />
                {errors.tpa && <div className="text-danger">{errors.tpa.message}</div>}
              </FormGroup>
              <FormGroup>
                <Label for="lineOfBusiness">Line Of Business</Label>
                <Controller
                  name="lineOfBusiness"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      options={lineOfBusinessOptions}
                      placeholder="Select Line Of Business"
                      className={errors.lineOfBusiness ? "is-invalid" : ""}
                      value={lineOfBusinessOptions.find(opt => opt.value === field.value) || null}
                      onChange={opt => field.onChange(opt ? opt.value : '')}
                    />
                  )}
                />
                {errors.lineOfBusiness && <div className="text-danger">{errors.lineOfBusiness.message}</div>}
              </FormGroup>
              <FormGroup>
                <Label for="claimType">Claim Type</Label>
                <Controller
                  name="claimType"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      options={claimTypeOptions}
                      placeholder="Select Claim Type"
                      className={errors.claimType ? "is-invalid" : ""}
                      value={claimTypeOptions.find(opt => opt.value === field.value) || null}
                      onChange={opt => field.onChange(opt ? opt.value : '')}
                    />
                  )}
                />
                {errors.claimType && <div className="text-danger">{errors.claimType.message}</div>}
              </FormGroup>
              <FormGroup>
                <Label for="communicationChannel">Communication Channel</Label>
                <Controller
                  name="communicationChannel"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      options={commChannelOptions}
                      placeholder="Select Communication Channel"
                      className={errors.communicationChannel ? "is-invalid" : ""}
                      value={commChannelOptions.find(opt => opt.value === field.value) || null}
                      onChange={opt => field.onChange(opt ? opt.value : '')}
                    />
                  )}
                />
                {errors.communicationChannel && <div className="text-danger">{errors.communicationChannel.message}</div>}
              </FormGroup>
              <FormGroup>
                <Label for="startDate">Start Date</Label>
                <Controller
                  name="startDate"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Input
                      id="startDate"
                      type="date"
                      value={field.value ?? ""}
                      onChange={e => field.onChange(e.target.value)}
                      invalid={!!errors.startDate}
                    />
                  )}
                />
                {errors.startDate && <div className="text-danger">{errors.startDate.message}</div>}
              </FormGroup>
              <FormGroup>
                <Label for="endDate">End Date</Label>
                <Controller
                  name="endDate"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Input
                      id="endDate"
                      type="date"
                      value={field.value ?? ""}
                      onChange={e => field.onChange(e.target.value)}
                      invalid={!!errors.endDate}
                    />
                  )}
                />
                {errors.endDate && <div className="text-danger">{errors.endDate.message}</div>}
              </FormGroup>
              {mutationError && <Alert color="danger">{mutationError}</Alert>}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>Cancel</Button>
          <Button color="primary" type="submit" disabled={creating || updating}>
            {isUpdate ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}
export default CreateUpdateTPAConfigModal
